import {useEffect, useState} from "react";
import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useBoundStore} from "../../../Store/store";
import {BaseCard} from "../../../Components/Cards/BaseCard";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import {NumberInput} from "../../../Components/Form/NumberInput";
import {PrimaryButton} from "../../../Components/Buttons/PrimaryButton";
import {useTranslation} from "react-i18next";
import {DangerButton} from "../../../Components/Buttons/DangerButton";
import {TextInput} from "../../../Components/Form/TextInput";
import {SelectInput} from "../../../Components/Form/SelectInput";
import {ControlledSwitch} from "../../../Components/Form/ControlledSwitch";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faDownload, faUpload, faTrash, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {ShiftsTable} from "../../../Components/Tables/TableCards/ShiftsTable";
import {ContractsTable} from "../../../Components/Tables/TableCards/ContractsTable";
import {InvoicesTable} from "../../../Components/Tables/TableCards/InvoicesTable";
import {PerformanceReportsTable} from "../../../Components/Tables/TableCards/PerformanceReportsTable";
import toast from "react-hot-toast";
import {DeleteConfirmDialog} from "../../../Components/Dialogs/Confirmation/DeleteConfirmDialog";
import MediaSection from "../../../Components/Cards/MediaSection";
import {MailHistoryList} from "../../../Components/MailHistory/MailHistoryList";
import {GeneratePerformanceStatementDialog} from "../../../Components/Dialogs/PerformanceReports/GeneratePerformanceStatementDialog";
import {ExceptionRatesCard} from "../../../Components/Cards/ExceptionRatesCard";
import {isMyCcpSubdomain} from "../../../Utils/SubdomainHelper";
import * as Sentry from "@sentry/react";


export const AdminFreelancerDetails = () => {
    const {
        freelancerId,
    } = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const getFreelancerDetail = useBoundStore((state) => state.getFreelancerDetail);
    const freelancerDetail = useBoundStore((state) => state.freelancerDetail);
    const deleteFreelancer = useBoundStore((state) => state.deleteFreelancer);
    const updateFreelancer = useBoundStore((state) => state.updateFreelancer);
    const markInvoiceAsPaid = useBoundStore((state) => state.markInvoiceAsPaid);
    const uploadFreelancerSignature = useBoundStore((state) => state.uploadFreelancerSignature);
    const deleteFreelancerSignature = useBoundStore((state) => state.deleteFreelancerSignature);

    const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
    const [openGenerateDialog, setOpenGenerateDialog] = React.useState(false);
    const [forceRenewData, setForceRenewData] = useState(false);
    const [freelancerLoading, setFreelancerLoading] = useState(true);
    const [signatureFile, setSignatureFile] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [isUploadingSignature, setIsUploadingSignature] = useState(false);
    const [isDeletingSignature, setIsDeletingSignature] = useState(false);
    const signatureInputRef = React.useRef(null);

    const handleDelete = () => {
        deleteFreelancer(freelancerId).then(() => {
            setOpenDeleteConfirm(false);
            toast.success(t('freelancers_module.toast_deleted'));
            navigate('/admin/freelancers');
        })
    }

    const handleMarkAsPaid = async (invoiceId) => {
        const success = await markInvoiceAsPaid(invoiceId);
        if (success) {
            setForceRenewData(!forceRenewData);
        }
        return success;
    };

    const handleGenerateSuccess = () => {
        setForceRenewData(!forceRenewData);
    };

    const handleSignatureFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error(t('freelancer_detail.image_only'));
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                toast.error(t('freelancer_detail.file_max_5mb'));
                return;
            }

            setSignatureFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignaturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadSignature = async () => {
        if (!signatureFile) {
            toast.error(t('freelancer_detail.select_file'));
            return;
        }

        setIsUploadingSignature(true);
        const result = await uploadFreelancerSignature(freelancerId, signatureFile);
        setIsUploadingSignature(false);

        if (result) {
            setSignatureFile(null);
            setSignaturePreview(null);
            if (signatureInputRef.current) {
                signatureInputRef.current.value = '';
            }
            await getFreelancerDetail(freelancerId);
        }
    };

    const handleClearSignature = () => {
        setSignatureFile(null);
        setSignaturePreview(null);
        if (signatureInputRef.current) {
            signatureInputRef.current.value = '';
        }
    };

    const handleDeleteSignature = async () => {
        if (!freelancerDetail?.media?.signature?.url) {
            return;
        }

        if (!confirm(t('freelancer_detail.confirm_delete_signature'))) {
            return;
        }

        setIsDeletingSignature(true);
        const result = await deleteFreelancerSignature(freelancerId);
        setIsDeletingSignature(false);

        if (result) {
            await getFreelancerDetail(freelancerId);
        }
    };

    const jobFunctions = [
        {value: 'A1', label: t('functions.A1'), id: 0},
        {value: 'A2', label: t('functions.A2'), id: 1},
        {value: 'A0', label: t('functions.A0'), id: 2},
        {value: 'A3', label: t('functions.A3'), id: 3}
    ]

    const mainStatutenOptions = [
        {value: 'freelance', label: t('statuten.freelance'), id: 0},
        {value: 'interim', label: t('statuten.interim'), id: 1},
    ];

    const extraStatutenByMain = {
        freelance: [
            {value: 'interim', label: t('statuten.interim'), id: 1},
        ],
        interim: [
            {value: 'student', label: t('statuten.student'), id: 2},
            {value: 'flexi_job', label: t('statuten.flexi_job'), id: 3},
        ],
    };

    useEffect(() => {
        const fetchFreelancerDetails = async () => {
            setFreelancerLoading(true);
            await getFreelancerDetail(freelancerId);
            setFreelancerLoading(false);
        };

        fetchFreelancerDetails();
    }, [getFreelancerDetail, freelancerId]);

    useEffect(() => {
        if(freelancerDetail){
            reset({
                firstname: freelancerDetail?.firstname ?? null,
                lastname: freelancerDetail?.lastname ?? null,
                email: freelancerDetail?.email ?? null,
                experience_from_year: freelancerDetail?.experience_from_year ?? null,
                function: freelancerDetail?.function ?? null,
                hourly_rate: freelancerDetail?.hourly_rate ?? null,
                street: freelancerDetail?.branches?.[0]?.street ?? null,
                house_number: freelancerDetail?.branches?.[0]?.number ?? null,
                bus: freelancerDetail?.branches?.[0]?.bus ?? null,
                city: freelancerDetail?.branches?.[0]?.city ?? null,
                postal: freelancerDetail?.branches?.[0]?.postal ?? null,
                country: freelancerDetail?.branches?.[0]?.country ?? null,
                company_name: freelancerDetail?.branches?.[0]?.company_name ?? null,
                vat_number: freelancerDetail?.branches?.[0]?.vat_number ?? null,
                tel: freelancerDetail?.branches?.[0]?.contact_tel ?? null,
                media: freelancerDetail.media ?? null,
                bank_account_number: freelancerDetail?.bank_account_number ?? null,
                relation_code_exact: freelancerDetail?.branches?.[0]?.relation_code_exact ?? null,
                main_statuut: freelancerDetail?.main_statuut ?? 'freelance',
                extra_statuten: freelancerDetail?.extra_statuten ?? [],
                communication_opt_out: freelancerDetail?.communication_opt_out ?? false,
            })
        }
    }, [freelancerDetail?.id]);

    const schema = yup.object({
        firstname: yup.string().required(),
        lastname: yup.string().required(),
        email: yup.string().email().required(),
        experience_from_year: yup.number().required(t('general.errors.experience_empty')),
        hourly_rate: yup.number().required(),
        function: yup.string().required(t('general.errors.function_empty')),
        street: yup.string().required(t('general.errors.street_empty')),
        house_number: yup.string().required(t('general.errors.number_empty')),
        bus: yup.string().nullable(),
        city: yup.string().required(t('general.errors.city_empty')),
        postal: yup.string().required(t('general.errors.zip_empty')),
        country: yup.string().required(t('general.errors.country_empty')),
        company_name: yup.string().when('main_statuut', {
            is: 'freelance',
            then: (schema) => schema.required(t('general.errors.company_name_empty')),
            otherwise: (schema) => schema.nullable(),
        }),
        vat_number: yup.string().when('main_statuut', {
            is: 'freelance',
            then: (schema) => schema.required(t('general.errors.vat_empty')),
            otherwise: (schema) => schema.nullable(),
        }),
        tel: yup.string().nullable(),
        bank_account_number: yup.string().required(),
        relation_code_exact: yup.string().when('main_statuut', {
            is: 'freelance',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        main_statuut: yup.string().oneOf(['freelance', 'interim']).required(),
        extra_statuten: yup.array().of(yup.string().oneOf(['freelance', 'interim', 'student', 'flexi_job'])).nullable(),
        communication_opt_out: yup.boolean(),
    }).required()

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        reset,
        watch,
        getValues,
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            firstname: freelancerDetail?.firstname ?? null,
            lastname: freelancerDetail?.lastname ?? null,
            email: freelancerDetail?.email ?? null,
            experience_from_year: freelancerDetail?.experience_from_year ?? null,
            function: freelancerDetail?.function ?? null,
            hourly_rate: freelancerDetail?.hourly_rate ?? null,
            street: freelancerDetail?.branches?.[0]?.street ?? null,
            house_number: freelancerDetail?.branches?.[0]?.number ?? null,
            bus: freelancerDetail?.branches?.[0]?.bus ?? null,
            city: freelancerDetail?.branches?.[0]?.city ?? null,
            postal: freelancerDetail?.branches?.[0]?.postal ?? null,
            country: freelancerDetail?.branches?.[0]?.country ?? null,
            company_name: freelancerDetail?.branches?.[0]?.company_name ?? null,
            vat_number: freelancerDetail?.branches?.[0]?.vat_number ?? null,
            tel: freelancerDetail?.branches?.[0]?.contact_tel ?? null,
            media: freelancerDetail.media ?? null,
            bank_account_number: freelancerDetail?.branches?.[0]?.bank_account ?? null,
            relation_code_exact: freelancerDetail?.relation_code_exact ?? null,
            main_statuut: freelancerDetail?.main_statuut ?? 'freelance',
            extra_statuten: freelancerDetail?.extra_statuten ?? [],
            communication_opt_out: freelancerDetail?.communication_opt_out ?? false,
        }
    });

    const hourlyRateValue = watch('hourly_rate');
    const selectedMainStatuut = watch('main_statuut');

    useEffect(() => {
        const allowed = extraStatutenByMain[selectedMainStatuut] ?? [];
        const allowedValues = allowed.map(o => o.value);
        const current = getValues('extra_statuten') || [];
        const filtered = current.filter(v => allowedValues.includes(v));
        if (filtered.length !== current.length) {
            setValue('extra_statuten', filtered);
        }
    }, [selectedMainStatuut]);

    const onSubmit = (data) => {
        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            freelancerId: freelancerId,
            is_published: data.published === "" ? false : data.published,
            experience: data.experience_from_year,
        }

        updateFreelancer(freelancerId, transformedData)
            .then(() => {
                toast.success(t('freelancers_module.toast_updated'));
                getFreelancerDetail(freelancerId);
            })
            .catch((err) => {
                Sentry.captureException(err);
                toast.error(t('freelancers_module.toast_update_error'));
            });
    }

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4 items-center'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('freelancers_module.detail_title')}</h1>
                        <div className={"flex flex-row group items-center gap-2"} onClick={() => {
                            navigate('/admin/freelancers');
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft} className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'}/>
                            <div className={'text-sm text-gray-500 my-auto cursor-pointer'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>
                    <div>
                        <DangerButton onClick={() => setOpenDeleteConfirm(true)}>{t('general.actions.remove_freelancer')}</DangerButton>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <div className={'md:col-span-3 space-y-4'}>
                    <BaseCard border={false}>
                        {freelancerLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="loader">{t('freelancers_module.loading')}</div>
                            </div>
                        ) : (
                            <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('general.labels.global_information')}</h2>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'firstname'}
                                    register={register}
                                    translationIndex={'general.fields.firstname'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'lastname'}
                                    register={register}
                                    translationIndex={'general.fields.lastname'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <TextInput
                                    name={'email'}
                                    register={register}
                                    translationIndex={'general.fields.email'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <TextInput
                                    name={'tel'}
                                    register={register}
                                    translationIndex={'general.fields.tel'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <NumberInput
                                    name={'experience_from_year'}
                                    register={register}
                                    translationIndex={'general.fields.experience'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <SelectInput
                                    label={'Function'}
                                    name={'function'}
                                    register={register}
                                    control={control}
                                    setValue={setValue}
                                    translationIndex={'general.fields.function'}
                                    options={jobFunctions}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <NumberInput
                                    name={'hourly_rate'}
                                    register={register}
                                    step={0.01}
                                    translationIndex={'general.fields.hourly_rate'}
                                    errors={errors}
                                />
                                {!isMyCcpSubdomain() && <span className={'text-sm text-gray-500'}><strong>{t('registrations.external_rate')}:</strong> {(Math.round(hourlyRateValue * 1.18 * 100) / 100)
                                    .toFixed(2)
                                    .replace('.', ',') || '-'}</span>}
                            </div>

                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-8'}>{t('freelancers_module.statuten_heading')}</h2>
                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-2 gap-4 items-end'}>
                                <SelectInput
                                    name={'main_statuut'}
                                    control={control}
                                    setValue={setValue}
                                    options={mainStatutenOptions}
                                    currentValue={getValues('main_statuut') ?? 'freelance'}
                                    translationIndex={'general.fields.main_statuut'}
                                    errors={errors}
                                />
                                <SelectInput
                                    name={'extra_statuten'}
                                    control={control}
                                    setValue={setValue}
                                    multiple={true}
                                    options={extraStatutenByMain[selectedMainStatuut] ?? []}
                                    currentValue={getValues('extra_statuten') ?? []}
                                    translationIndex={'general.fields.extra_statuten'}
                                    errors={errors}
                                />
                            </div>

                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-8'}>{t('freelancers_module.communication_heading')}</h2>
                            <div className="mb-2 flex flex-row items-center">
                                <ControlledSwitch
                                    control={control}
                                    name={'communication_opt_out'}
                                    getValues={getValues}
                                    setValue={setValue}
                                    errors={errors}
                                    translationIndex={'freelancers_module.communication_opt_out'}
                                />
                                <span className={'ml-2 text-gray-400 cursor-help'}
                                      title={t('freelancers_module.communication_opt_out_info')}>
                                    <FontAwesomeIcon icon={faCircleInfo}/>
                                </span>
                            </div>

                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-8'}>{t('general.labels.address')}</h2>
                            <div className="mb-2">
                                <TextInput
                                    name={'street'}
                                    register={register}
                                    translationIndex={'general.fields.street'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'house_number'}
                                    register={register}
                                    translationIndex={'general.fields.nr'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'bus'}
                                    register={register}
                                    translationIndex={'general.fields.bus'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'city'}
                                    register={register}
                                    translationIndex={'general.fields.city'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'postal'}
                                    register={register}
                                    translationIndex={'general.fields.postal_code'}
                                    errors={errors}
                                />
                            </div>


                            {selectedMainStatuut === 'freelance' && (
                                <>
                                    <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-8'}>{t('general.labels.company_information')}</h2>
                                    <div className="mb-2">
                                        <TextInput
                                            name={'company_name'}
                                            register={register}
                                            translationIndex={'general.fields.name'}
                                            errors={errors}
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <TextInput
                                            name={'vat_number'}
                                            register={register}
                                            translationIndex={'general.fields.vat_number'}
                                            errors={errors}
                                        />
                                    </div>
                                </>
                            )}

                            <div className={'mt-4 mb-4'}>
                                <TextInput
                                    label={'Bank account number'}
                                    name={'bank_account_number'}
                                    register={register}
                                    translationIndex={'general.fields.bank_account_number'}
                                    errors={errors}
                                />
                            </div>

                            {selectedMainStatuut === 'freelance' && (
                                <div className={'mt-4 mb-4'}>
                                    <TextInput
                                        label={'Relatiecode Exact'}
                                        name={'relation_code_exact'}
                                        register={register}
                                        translationIndex={'general.fields.relation_code_exact'}
                                        errors={errors}
                                    />
                                </div>
                            )}

                            <div className={'mt-4 mb-4'}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('freelancer_detail.rejection_reason')}
                                </label>
                                <p className="text-sm text-gray-600">
                                    {freelancerDetail?.rejection_remark || t('registrations.no_comment')}
                                </p>
                            </div>

                            <div className={'mt-4'}>
                                <PrimaryButton onClick={handleSubmit(onSubmit)}>
                                    {t('freelancer_detail.save_freelancer')}
                                </PrimaryButton>
                            </div>
                        </form>
                        )}
                    </BaseCard>

                    {
                        freelancerDetail?.media && (
                            <BaseCard border={false}>
                                <h3 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>Media</h3>
                                <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                    <div
                                        className="mb-4 flex flex-row items-center justify-start"
                                        key={'diploma_freelancer'}>
                                        <p className="font-bold leading-3 underline mr-2">{t(`general.fields.diploma`)}:</p>
                                        {
                                            freelancerDetail?.media?.diploma?.url ? (
                                                <a href={freelancerDetail?.media?.diploma?.url} target="_blank"
                                                   className="group"
                                                   download={true}>
                                                    <FontAwesomeIcon className="group-hover:text-primary"
                                                                     icon={faDownload}/>
                                                </a>
                                            ) : (
                                                <span>{t('general.labels.no_file')}</span>
                                            )
                                        }
                                    </div>

                                    <div
                                        className="mb-4 flex flex-row items-center justify-start"
                                        key={'visa_freelancer'}>
                                        <p className="font-bold leading-3 underline mr-2">{t(`general.fields.visa`)}:</p>
                                        {
                                            freelancerDetail?.media?.visa?.url ? (
                                                <a href={freelancerDetail?.media?.visa?.url} target="_blank"
                                                   className="group"
                                                   download={true}>
                                                    <FontAwesomeIcon className="group-hover:text-primary"
                                                                     icon={faDownload}/>
                                                </a>
                                            ) : (
                                                <span>{t('general.labels.no_file')}</span>
                                            )
                                        }
                                    </div>
                                </div>

                            </BaseCard>
                        )
                    }


                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('shifts.title')}</h2>
                        <ShiftsTable userId={freelancerId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('contracts.title')}</h2>
                        <ContractsTable userId={freelancerId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('invoices.title')}</h2>
                        <InvoicesTable userId={freelancerId} showActions={true} onMarkAsPaid={handleMarkAsPaid} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('performance_reports.title')}</h2>
                        <PerformanceReportsTable
                            userId={freelancerId}
                            forceNewData={forceRenewData}
                            showGenerateButton={true}
                            onGenerateClick={() => setOpenGenerateDialog(true)}
                        />
                    </BaseCard>

                    <ExceptionRatesCard freelancerId={freelancerId} />

                    <BaseCard border={false}>
                        <MediaSection media={freelancerDetail?.media ?? {}} />
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('freelancer_detail.signature')}</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <div className="mb-2">
                                    <label className="block text-gray-700 font-medium mb-2">
                                        {t('freelancer_detail.current_signature')}
                                    </label>
                                    {freelancerDetail?.media?.signature?.url ? (
                                        <div className="space-y-3">
                                            <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                                                <img
                                                    src={freelancerDetail.media.signature.url}
                                                    alt="Freelancer Handtekening"
                                                    className="max-w-full h-32 object-contain mx-auto"
                                                />
                                            </div>
                                            <DangerButton
                                                onClick={handleDeleteSignature}
                                                disabled={isDeletingSignature || isUploadingSignature}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                {isDeletingSignature ? t('freelancer_detail.deleting') : t('general.actions.delete')}
                                            </DangerButton>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-sm">{t('freelancer_detail.no_signature')}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="mb-2">
                                    <label className="block text-gray-700 font-medium mb-2">
                                        {signaturePreview ? t('freelancer_detail.new_signature') : t('freelancer_detail.upload_signature')}
                                    </label>

                                    {signaturePreview && (
                                        <div className="border-2 border-primary border-dashed rounded-lg p-4 bg-primary/5 mb-3">
                                            <img
                                                src={signaturePreview}
                                                alt="Handtekening Preview"
                                                className="max-w-full h-32 object-contain mx-auto"
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        <label className="cursor-pointer block">
                                            <input
                                                ref={signatureInputRef}
                                                type="file"
                                                accept="image/png, image/jpg, image/jpeg, image/gif, image/webp"
                                                onChange={handleSignatureFileChange}
                                                className="hidden"
                                                disabled={isUploadingSignature || isDeletingSignature}
                                            />
                                            <div className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 border border-gray-300 w-full">
                                                <FontAwesomeIcon icon={faUpload} />
                                                <span className="text-sm font-medium">
                                                    {signatureFile ? signatureFile.name : t('freelancer_detail.choose_file')}
                                                </span>
                                            </div>
                                        </label>

                                        <div className="bg-gray-50 border border-gray-200 rounded p-2">
                                            <div className="text-xs text-gray-600">
                                                {t('freelancer_detail.allowed_file_types')}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                {t('freelancer_detail.max_file_size')}
                                            </div>
                                        </div>

                                        {signatureFile && (
                                            <div className="flex gap-2">
                                                <PrimaryButton
                                                    onClick={handleUploadSignature}
                                                    disabled={isUploadingSignature || isDeletingSignature}
                                                >
                                                    {isUploadingSignature ? t('freelancer_detail.uploading') : t('general.actions.save')}
                                                </PrimaryButton>
                                                <button
                                                    onClick={handleClearSignature}
                                                    disabled={isUploadingSignature || isDeletingSignature}
                                                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                                                >
                                                    {t('general.actions.cancel')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </BaseCard>

                    <BaseCard border={false}>
                        <MailHistoryList filters={{freelancerId: freelancerId}} />
                    </BaseCard>
                </div>
            </div>

            <DeleteConfirmDialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)} onConfirm={handleDelete}/>
            <GeneratePerformanceStatementDialog
                isOpen={openGenerateDialog}
                onClose={() => setOpenGenerateDialog(false)}
                freelancerId={freelancerId}
                onSuccess={handleGenerateSuccess}
            />
        </div>
    )
}
